import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableBody as MuiTableBody,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { repAPI, orderAPI, productAPI, adminAPI, clientShippingAddressesAPI } from '../../services/api';
import { calculateTieredPrice } from '../../utils/tieredPricing';

// Shipping method options
const SHIPPING_METHOD_OPTIONS = [
  { value: 'FEDEX_FIRST_OVERNIGHT', label: 'FedEx First Overnight (by 8:30am)' },
  { value: 'FEDEX_STANDARD_OVERNIGHT', label: 'FedEx Standard Overnight (by 10:30am)' },
  { value: 'UPS_NEXT_DAY_AIR_EARLY', label: 'UPS Next Day Air Early (by 9:00am)' },
  { value: 'UPS_NEXT_DAY_AIR', label: 'UPS Next Day Air (by 12:00pm)' },
];

const getShippingMethodLabel = (value) => {
  const match = SHIPPING_METHOD_OPTIONS.find((option) => option.value === value);
  return match ? match.label : value;
};

const getDefaultDeliveryDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

const RepOrders = () => {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Place Order Dialog - Multi-step wizard
  const [placeOrderOpen, setPlaceOrderOpen] = useState(false);
  const [orderStep, setOrderStep] = useState(0);
  const [placeOrderData, setPlaceOrderData] = useState({
    clientUserId: '',
    items: [{ productId: '', quantity: 1, unitPrice: 0 }],
    shippingAddress: {
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: ''
    },
    inventorySource: 'warehouse', // 'rep-stock' or 'warehouse'
    selectedRepInventory: {}, // Map of productId -> inventoryId for rep stock
    deliveryDate: '', // Required when shipping from warehouse
    shippingMethod: 'FEDEX_FIRST_OVERNIGHT', // Required when shipping from warehouse
    notes: ''
  });
  const [repInventory, setRepInventory] = useState([]);
  const [loadingRepInventory, setLoadingRepInventory] = useState(false);
  
  // Client shipping addresses state
  const [clientShippingAddresses, setClientShippingAddresses] = useState([]);
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState('');
  const [useNewShippingAddress, setUseNewShippingAddress] = useState(false);

  const orderSteps = [
    { label: 'Select Client', icon: <PersonIcon /> },
    { label: 'Order Items', icon: <ShoppingBagIcon /> },
    { label: 'Shipping Address', icon: <ShippingIcon /> },
    { label: 'Inventory Source', icon: <InventoryIcon /> },
    { label: 'Review', icon: <CheckCircleIcon /> },
  ];

  // Edit Order Dialog
  const [editOrderOpen, setEditOrderOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderData, setEditOrderData] = useState({});

  useEffect(() => {
    fetchOrders();
    fetchClients();
    // Don't load products until a client is selected
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getRepOrders();
      if (response.success) {
        setOrders(response.data);
      }
    } catch (err) {
      setError('Failed to load orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await repAPI.getMyClients();
      if (response.success) {
        setClients(response.data);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const fetchProducts = async (organization) => {
    try {
      const params = organization ? { organization } : {};
      const response = await productAPI.getProducts(params);
      if (response.success) {
        setProducts(response.data);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const fetchRepInventory = async () => {
    try {
      setLoadingRepInventory(true);
      const response = await repAPI.getMyOwnInventory();
      if (response.success) {
        setRepInventory(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load rep inventory:', err);
      setRepInventory([]);
    } finally {
      setLoadingRepInventory(false);
    }
  };

  const handleClientSelect = async (clientId) => {
    if (!clientId) {
      setPlaceOrderData({
        ...placeOrderData,
        clientUserId: '',
        shippingAddress: {
          name: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zipCode: '',
          phone: ''
        }
      });
      setProducts([]);
      return;
    }

    // Find the selected client from the clients list
    const selectedClient = clients.find(c => c.id === clientId);
    
    if (selectedClient) {
      // Pre-fill shipping address from client profile
      // Handle both nested profile and flattened structure
      console.log('Selected client data:', selectedClient);
      const profile = selectedClient.profile || {};
      const address = profile.address || selectedClient.address || '';
      const city = profile.city || selectedClient.city || '';
      const state = profile.state || selectedClient.state || '';
      const zipCode = profile.zipCode || selectedClient.zipCode || '';
      
      // Build full name - prefer clinic name if available, otherwise use person name
      let fullName = '';
      if (selectedClient.clinicName) {
        // If we have a clinic name, use person name with title
        const personName = `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim();
        const title = profile.title || selectedClient.title || '';
        fullName = personName ? `${personName}${title ? ', ' + title : ''}` : selectedClient.clinicName;
      } else {
        fullName = `${selectedClient.firstName || ''} ${selectedClient.lastName || ''}`.trim() || selectedClient.email;
      }
      
      console.log('Address fields:', { address, city, state, zipCode, fullName });
      
      setPlaceOrderData({
        ...placeOrderData,
        clientUserId: clientId,
        shippingAddress: {
          name: fullName,
          address1: address || '',
          address2: '',
          city: city || '',
          state: state || '',
          zipCode: zipCode || '',
          phone: selectedClient.phone || ''
        },
        items: [{ productId: '', quantity: 1, unitPrice: 0 }]
      });

      // Load products for the client's organization
      // Get organization from client data or default to EPL
      const clientOrg = selectedClient.organization || 'EPL';
      await fetchProducts(clientOrg);
      
      // Load client shipping addresses
      try {
        const addressResponse = await clientShippingAddressesAPI.getAddresses(clientId);
        if (addressResponse.success && addressResponse.data && addressResponse.data.length > 0) {
          setClientShippingAddresses(addressResponse.data);
          const defaultAddress = addressResponse.data.find(addr => addr.isDefault) || addressResponse.data[0];
          if (defaultAddress) {
            setSelectedShippingAddressId(defaultAddress.id);
            setUseNewShippingAddress(false);
            setPlaceOrderData({
              ...placeOrderData,
              clientUserId: clientId,
              shippingAddress: {
                name: defaultAddress.contactName || defaultAddress.name || fullName,
                address1: defaultAddress.address1 || '',
                address2: defaultAddress.address2 || '',
                city: defaultAddress.city || '',
                state: defaultAddress.state || '',
                zipCode: defaultAddress.zipCode || '',
                phone: defaultAddress.phone || selectedClient.phone || ''
              }
            });
          }
        } else {
          setClientShippingAddresses([]);
          setUseNewShippingAddress(true);
        }
      } catch (error) {
        console.error('Failed to load client shipping addresses:', error);
        setClientShippingAddresses([]);
        setUseNewShippingAddress(true);
      }
    } else {
      // If client not found in list, try to fetch from API
      try {
        const response = await adminAPI.getUserById(clientId);
        if (response.success && response.data) {
          const client = response.data;
          const profile = client.profile || {};
          const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email;
          
          setPlaceOrderData({
            ...placeOrderData,
            clientUserId: clientId,
            shippingAddress: {
              name: fullName,
              address1: profile.address || '',
              address2: '',
              city: profile.city || '',
              state: profile.state || '',
              zipCode: profile.zipCode || '',
              phone: client.phone || ''
            },
            items: [{ productId: '', quantity: 1, unitPrice: 0 }]
          });

          const clientOrg = client.organization || 'EPL';
          await fetchProducts(clientOrg);
        }
      } catch (err) {
        console.error('Failed to load client details:', err);
        setError('Failed to load client details');
      }
    }
  };

  const handlePlaceOrder = async () => {
    try {
      if (!placeOrderData.clientUserId || placeOrderData.items.length === 0) {
        setError('Please select a client and add at least one item');
        return;
      }

      // Prepare order data with inventory source information
      const orderPayload = {
        clientUserId: placeOrderData.clientUserId,
        items: placeOrderData.items.map(item => ({
          productId: item.productId,
          quantity: parseInt(item.quantity) || 1,
          unitPrice: parseFloat(item.unitPrice) || 0
        })),
        shippingAddress: placeOrderData.shippingAddress,
        // Include clientShippingAddressId if a saved address was selected
        clientShippingAddressId: !useNewShippingAddress && selectedShippingAddressId ? selectedShippingAddressId : undefined,
        notes: placeOrderData.notes,
        inventorySource: placeOrderData.inventorySource,
        ...(placeOrderData.inventorySource === 'rep-stock' && {
          selectedRepInventory: placeOrderData.selectedRepInventory
        }),
        ...(placeOrderData.inventorySource === 'warehouse' && {
          deliveryDate: placeOrderData.deliveryDate,
          shippingMethod: placeOrderData.shippingMethod
        })
      };

      const response = await orderAPI.placeOrderForClient(orderPayload);
      if (response.success) {
        setSuccess('Order placed successfully!');
        setPlaceOrderOpen(false);
        setOrderStep(0);
        setPlaceOrderData({
          clientUserId: '',
          items: [{ productId: '', quantity: 1, unitPrice: 0 }],
          shippingAddress: {
            name: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zipCode: '',
            phone: ''
          },
          inventorySource: 'warehouse',
          selectedRepInventory: {},
          notes: ''
        });
        setProducts([]);
        setRepInventory([]);
        fetchOrders();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to place order');
    }
  };

  const handleEditOrder = async () => {
    try {
      if (!editingOrder) return;

      // Check if order is shipped
      if (editingOrder.shippedAt || editingOrder.status === 'SHIPPED' || editingOrder.status === 'DELIVERED') {
        setError('Cannot edit order after it has been shipped');
        return;
      }

      const response = await orderAPI.updateOrder(editingOrder.id, editOrderData);
      if (response.success) {
        setSuccess('Order updated successfully!');
        setEditOrderOpen(false);
        setEditingOrder(null);
        fetchOrders();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update order');
    }
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setEditOrderData({
      notes: order.notes || '',
      shippingAddress: {
        name: order.shippingName || '',
        address1: order.shippingAddress1 || '',
        city: order.shippingCity || '',
        state: order.shippingState || '',
        zipCode: order.shippingZipCode || '',
        phone: order.shippingPhone || ''
      }
    });
    setEditOrderOpen(true);
  };

  const addOrderItem = () => {
    setPlaceOrderData({
      ...placeOrderData,
      items: [...placeOrderData.items, { productId: '', quantity: 1, unitPrice: 0 }]
    });
  };

  // Calculate total quantity for tiered pricing
  const totalQuantity = useMemo(() => {
    return placeOrderData.items.reduce((total, item) => {
      return total + (parseInt(item.quantity) || 0);
    }, 0);
  }, [placeOrderData.items]);

  // Calculate tiered price per unit
  const tieredPricePerUnit = useMemo(() => {
    return calculateTieredPrice(totalQuantity);
  }, [totalQuantity]);

  const updateOrderItem = (index, field, value) => {
    const newItems = [...placeOrderData.items];
    newItems[index][field] = value;
    
    // If product changed, update price using tiered pricing
    if (field === 'productId' && value) {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct) {
        // Calculate total quantity including this item
        const currentTotalQty = newItems.reduce((sum, item, idx) => {
          if (idx === index) {
            return sum + (parseInt(value ? newItems[index].quantity : 0) || 0);
          }
          return sum + (parseInt(item.quantity) || 0);
        }, 0);
        const tieredPrice = calculateTieredPrice(currentTotalQty);
        newItems[index].unitPrice = tieredPrice;
      }
    }
    
    // If quantity changed, recalculate tiered pricing for all items
    if (field === 'quantity') {
      const newTotalQty = newItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
      const newTieredPrice = calculateTieredPrice(newTotalQty);
      // Update all items to use the new tiered price (unless manually overridden)
      newItems.forEach((item, idx) => {
        if (!item.isPriceOverridden) {
          newItems[idx].unitPrice = newTieredPrice;
        }
      });
    }
    
    setPlaceOrderData({ ...placeOrderData, items: newItems });
  };

  const calculateOrderTotal = () => {
    // Use tiered pricing for calculation
    return placeOrderData.items.reduce((total, item) => {
      const quantity = parseInt(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || tieredPricePerUnit;
      return total + (quantity * price);
    }, 0);
  };

  const canProceedToNextStep = () => {
    switch (orderStep) {
      case 0: // Select Client
        return !!placeOrderData.clientUserId;
      case 1: // Order Items
        return placeOrderData.items.every(item => 
          item.productId && item.quantity > 0 && item.unitPrice > 0
        ) && placeOrderData.items.length > 0;
      case 2: // Shipping Address
        return !!(
          placeOrderData.shippingAddress.name &&
          placeOrderData.shippingAddress.address1 &&
          placeOrderData.shippingAddress.city &&
          placeOrderData.shippingAddress.state &&
          placeOrderData.shippingAddress.zipCode
        );
      case 3: // Inventory Source
        if (placeOrderData.inventorySource === 'rep-stock') {
          // If rep stock, need to verify inventory is available for each item
          return placeOrderData.items.every(item => {
            if (!item.productId) return false;
            const selectedInv = placeOrderData.selectedRepInventory[item.productId];
            return selectedInv && repInventory.find(inv => inv.id === selectedInv);
          });
        } else {
          // Warehouse requires delivery date and shipping method
          return !!(placeOrderData.deliveryDate && placeOrderData.shippingMethod);
        }
      case 4: // Review
        return true;
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (orderStep === 3 && placeOrderData.inventorySource === 'rep-stock' && repInventory.length === 0) {
      fetchRepInventory();
    }
    if (canProceedToNextStep()) {
      setOrderStep(prev => Math.min(prev + 1, orderSteps.length - 1));
    }
  };

  const handleBackStep = () => {
    setOrderStep(prev => Math.max(prev - 1, 0));
  };

  const removeOrderItem = (index) => {
    setPlaceOrderData({
      ...placeOrderData,
      items: placeOrderData.items.filter((_, i) => i !== index)
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'warning',
      CONFIRMED: 'info',
      PROCESSING: 'info',
      SHIPPED: 'primary',
      DELIVERED: 'success',
      CANCELLED: 'error',
    };
    return colors[status] || 'default';
  };

  const canEditOrder = (order) => {
    if (!order) return false;
    return !order.shippedAt && order.status !== 'SHIPPED' && order.status !== 'DELIVERED';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Client Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPlaceOrderOpen(true)}
          sx={{
            bgcolor: '#039582',
            '&:hover': { bgcolor: '#027a6b' },
            textTransform: 'none'
          }}
        >
          Place Order for Client
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tracking</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No orders found for your clients
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{order.orderNumber}</TableCell>
                  <TableCell>{order.user?.profile?.clinicName || order.user?.email}</TableCell>
                  <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>${parseFloat(order.totalAmount || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip label={order.status} color={getStatusColor(order.status)} size="small" />
                  </TableCell>
                  <TableCell>{order.trackingNumber || 'N/A'}</TableCell>
                  <TableCell align="right">
                    {canEditOrder(order) && (
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(order)}
                        sx={{ color: '#039582' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Place Order Dialog - Multi-step Wizard */}
      <Dialog 
        open={placeOrderOpen} 
        onClose={() => {
          setPlaceOrderOpen(false);
          setOrderStep(0);
          setPlaceOrderData({
            clientUserId: '',
            items: [{ productId: '', quantity: 1, unitPrice: 0 }],
            shippingAddress: {
              name: '',
              address1: '',
              address2: '',
              city: '',
              state: '',
              zipCode: '',
              phone: ''
            },
            inventorySource: 'warehouse',
            selectedRepInventory: {},
            deliveryDate: '',
            shippingMethod: 'FEDEX_FIRST_OVERNIGHT',
            notes: ''
          });
          setProducts([]);
          setRepInventory([]);
        }} 
        maxWidth="lg" 
        fullWidth
        onEnter={() => {
          if (clients.length === 0) {
            fetchClients();
          }
          // Initialize delivery date if not set
          if (!placeOrderData.deliveryDate && placeOrderData.inventorySource === 'warehouse') {
            setPlaceOrderData(prev => ({
              ...prev,
              deliveryDate: getDefaultDeliveryDate()
            }));
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography variant="h6">Place Order for Client</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, minHeight: '500px' }}>
          <Stepper activeStep={orderStep} sx={{ mb: 4 }}>
            {orderSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel icon={step.icon}>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Select Client */}
          {orderStep === 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <PersonIcon color="primary" />
                  Select Client
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Client</InputLabel>
                  <Select
                    value={placeOrderData.clientUserId}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    label="Client"
                  >
                    <MenuItem value="">
                      <em>Select a client...</em>
                    </MenuItem>
                    {clients.length === 0 ? (
                      <MenuItem disabled>Loading clients...</MenuItem>
                    ) : (
                      clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.clinicName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {placeOrderData.clientUserId && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Client selected. Click Next to add order items.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 1: Order Items */}
          {orderStep === 1 && (
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShoppingBagIcon color="primary" />
                    Order Items
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addOrderItem}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    Add Item
                  </Button>
                </Box>
                {!placeOrderData.clientUserId && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Please go back and select a client first
                  </Alert>
                )}
                <Stack spacing={2}>
                  {placeOrderData.items.map((item, index) => {
                    const selectedProduct = products.find(p => p.id === item.productId);
                    return (
                      <Card key={index} variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={5}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Product</InputLabel>
                              <Select
                                value={item.productId}
                                onChange={(e) => updateOrderItem(index, 'productId', e.target.value)}
                                label="Product"
                                disabled={!placeOrderData.clientUserId}
                              >
                                <MenuItem value="">
                                  <em>Select product...</em>
                                </MenuItem>
                                {products.map((product) => (
                                  <MenuItem key={product.id} value={product.id}>
                                    {product.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Quantity"
                              value={item.quantity}
                              onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              inputProps={{ min: 1 }}
                            />
                          </Grid>
                          <Grid item xs={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Unit Price"
                              value={item.unitPrice}
                              onChange={(e) => updateOrderItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                              }}
                              helperText={selectedProduct && `Tiered: $${tieredPricePerUnit.toFixed(2)} (${totalQuantity} vials)`}
                            />
                          </Grid>
                          <Grid item xs={2} md={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                              ${((parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeOrderItem(index)}
                              disabled={placeOrderData.items.length === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Card>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" align="right">
                    Subtotal: ${calculateOrderTotal().toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Shipping Address */}
          {orderStep === 2 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <ShippingIcon color="primary" />
                  Shipping Address
                </Typography>
                
                {/* Client Shipping Address Selection */}
                {placeOrderData.clientUserId && clientShippingAddresses.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                      Select Shipping Address
                    </Typography>
                    <RadioGroup
                      value={useNewShippingAddress ? 'new' : selectedShippingAddressId}
                      onChange={(e) => {
                        const addressId = e.target.value;
                        if (addressId === 'new') {
                          setUseNewShippingAddress(true);
                          setSelectedShippingAddressId('');
                        } else {
                          const selectedAddress = clientShippingAddresses.find(addr => addr.id === addressId);
                          if (selectedAddress) {
                            setUseNewShippingAddress(false);
                            setSelectedShippingAddressId(addressId);
                            setPlaceOrderData({
                              ...placeOrderData,
                              shippingAddress: {
                                name: selectedAddress.contactName || selectedAddress.name || '',
                                address1: selectedAddress.address1 || '',
                                address2: selectedAddress.address2 || '',
                                city: selectedAddress.city || '',
                                state: selectedAddress.state || '',
                                zipCode: selectedAddress.zipCode || '',
                                phone: selectedAddress.phone || ''
                              }
                            });
                          }
                        }
                      }}
                    >
                      {clientShippingAddresses.map((address) => (
                        <Paper key={address.id} sx={{ p: 2, mb: 1 }}>
                          <FormControlLabel
                            value={address.id}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {address.name} {address.isDefault && '(Default)'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {address.address1}, {address.city}, {address.state} {address.zipCode}
                                </Typography>
                              </Box>
                            }
                          />
                        </Paper>
                      ))}
                      <Paper sx={{ p: 2 }}>
                        <FormControlLabel
                          value="new"
                          control={<Radio />}
                          label="Use new address"
                        />
                      </Paper>
                    </RadioGroup>
                  </Box>
                )}
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={placeOrderData.shippingAddress.name}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, name: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={placeOrderData.shippingAddress.address1}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, address1: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2 (Optional)"
                      value={placeOrderData.shippingAddress.address2}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, address2: e.target.value }
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={placeOrderData.shippingAddress.city}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, city: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="State"
                      value={placeOrderData.shippingAddress.state}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, state: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={placeOrderData.shippingAddress.zipCode}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, zipCode: e.target.value }
                      })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={placeOrderData.shippingAddress.phone}
                      onChange={(e) => setPlaceOrderData({
                        ...placeOrderData,
                        shippingAddress: { ...placeOrderData.shippingAddress, phone: e.target.value }
                      })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Inventory Source */}
          {orderStep === 3 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <InventoryIcon color="primary" />
                  Inventory Source
                </Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={placeOrderData.inventorySource}
                    onChange={(e) => {
                      const newSource = e.target.value;
                      setPlaceOrderData({
                        ...placeOrderData,
                        inventorySource: newSource,
                        selectedRepInventory: {},
                        // Initialize delivery date if switching to warehouse
                        ...(newSource === 'warehouse' && !placeOrderData.deliveryDate && {
                          deliveryDate: getDefaultDeliveryDate()
                        }),
                        // Clear delivery date and shipping method if switching to rep stock
                        ...(newSource === 'rep-stock' && {
                          deliveryDate: '',
                          shippingMethod: 'FEDEX_FIRST_OVERNIGHT'
                        })
                      });
                      if (newSource === 'rep-stock' && repInventory.length === 0) {
                        fetchRepInventory();
                      }
                    }}
                  >
                    <FormControlLabel
                      value="warehouse"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={600}>Ship from Main Warehouse</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Products will be shipped from the main warehouse to the shipping address
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="rep-stock"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight={600}>Use My On-Hand Inventory</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Select from inventory you currently have in your possession
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {placeOrderData.inventorySource === 'warehouse' && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                      Shipping Information (Required for Warehouse Orders)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Requested Delivery Date"
                          type="date"
                          value={placeOrderData.deliveryDate}
                          onChange={(e) => setPlaceOrderData({
                            ...placeOrderData,
                            deliveryDate: e.target.value
                          })}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: getDefaultDeliveryDate() }}
                          required
                          helperText="Select the arrival date requested by the clinic"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Shipping Method</InputLabel>
                          <Select
                            value={placeOrderData.shippingMethod}
                            onChange={(e) => setPlaceOrderData({
                              ...placeOrderData,
                              shippingMethod: e.target.value
                            })}
                            label="Shipping Method"
                          >
                            {SHIPPING_METHOD_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      The warehouse needs this information to process and ship the order correctly.
                    </Alert>
                  </Box>
                )}

                {placeOrderData.inventorySource === 'rep-stock' && (
                  <Box sx={{ mt: 3 }}>
                    {loadingRepInventory ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : repInventory.length === 0 ? (
                      <Alert severity="warning">
                        You don't have any inventory in your stock. Please select warehouse shipping instead.
                      </Alert>
                    ) : (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 2 }}>
                          Select inventory for each product:
                        </Typography>
                        <Stack spacing={2}>
                          {placeOrderData.items.map((item, itemIndex) => {
                            if (!item.productId) return null;
                            const product = products.find(p => p.id === item.productId);
                            const availableInventory = repInventory.filter(inv => 
                              inv.productId === item.productId && 
                              inv.quantityAvailable >= item.quantity
                            );
                            return (
                              <Card key={itemIndex} variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {product?.name} (Qty: {item.quantity})
                                </Typography>
                                {availableInventory.length === 0 ? (
                                  <Alert severity="error">
                                    No available inventory for this product in your stock
                                  </Alert>
                                ) : (
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Select Inventory Batch</InputLabel>
                                    <Select
                                      value={placeOrderData.selectedRepInventory[item.productId] || ''}
                                      onChange={(e) => {
                                        setPlaceOrderData({
                                          ...placeOrderData,
                                          selectedRepInventory: {
                                            ...placeOrderData.selectedRepInventory,
                                            [item.productId]: e.target.value
                                          }
                                        });
                                      }}
                                      label="Select Inventory Batch"
                                    >
                                      {availableInventory.map((inv) => (
                                        <MenuItem key={inv.id} value={inv.id}>
                                          Batch: {inv.batchId || inv.id} | Qty Available: {inv.quantityAvailable || 0} | Exp: {inv.expirationDate ? new Date(inv.expirationDate).toLocaleDateString() : 'N/A'}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </Card>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Review */}
          {orderStep === 4 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CheckCircleIcon color="primary" />
                  Review Order
                </Typography>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Client</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {clients.find(c => c.id === placeOrderData.clientUserId)?.clinicName || 
                       clients.find(c => c.id === placeOrderData.clientUserId)?.email || 
                       'N/A'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Order Items</Typography>
                    <TableContainer>
                      <Table size="small">
                        <MuiTableHead>
                          <MuiTableRow>
                            <MuiTableCell>Product</MuiTableCell>
                            <MuiTableCell align="right">Quantity</MuiTableCell>
                            <MuiTableCell align="right">Unit Price</MuiTableCell>
                            <MuiTableCell align="right">Total</MuiTableCell>
                          </MuiTableRow>
                        </MuiTableHead>
                        <MuiTableBody>
                          {placeOrderData.items.map((item, index) => {
                            const product = products.find(p => p.id === item.productId);
                            return (
                              <MuiTableRow key={index}>
                                <MuiTableCell>{product?.name || 'N/A'}</MuiTableCell>
                                <MuiTableCell align="right">{item.quantity}</MuiTableCell>
                                <MuiTableCell align="right">${parseFloat(item.unitPrice || 0).toFixed(2)}</MuiTableCell>
                                <MuiTableCell align="right">
                                  ${((parseFloat(item.unitPrice) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                                </MuiTableCell>
                              </MuiTableRow>
                            );
                          })}
                        </MuiTableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                      <Typography variant="h6">
                        Total: ${calculateOrderTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Shipping Address</Typography>
                    <Typography variant="body2">
                      {placeOrderData.shippingAddress.name}<br />
                      {placeOrderData.shippingAddress.address1}<br />
                      {placeOrderData.shippingAddress.address2 && `${placeOrderData.shippingAddress.address2}<br />`}
                      {placeOrderData.shippingAddress.city}, {placeOrderData.shippingAddress.state} {placeOrderData.shippingAddress.zipCode}<br />
                      {placeOrderData.shippingAddress.phone && `Phone: ${placeOrderData.shippingAddress.phone}`}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Inventory Source</Typography>
                    <Typography variant="body2">
                      {placeOrderData.inventorySource === 'rep-stock' ? 'Rep On-Hand Inventory' : 'Main Warehouse'}
                    </Typography>
                    {placeOrderData.inventorySource === 'warehouse' && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Requested Delivery:</strong> {placeOrderData.deliveryDate ? new Date(placeOrderData.deliveryDate).toLocaleDateString() : 'Not specified'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Shipping Method:</strong> {getShippingMethodLabel(placeOrderData.shippingMethod)}
                        </Typography>
                      </>
                    )}
                  </Box>
                  {placeOrderData.notes && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Notes</Typography>
                        <Typography variant="body2">{placeOrderData.notes}</Typography>
                      </Box>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              setPlaceOrderOpen(false);
              setOrderStep(0);
            }} 
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Box>
            {orderStep > 0 && (
              <Button
                onClick={handleBackStep}
                startIcon={<ArrowBackIcon />}
                sx={{ textTransform: 'none', mr: 1 }}
              >
                Back
              </Button>
            )}
            {orderStep < orderSteps.length - 1 ? (
              <Button
                onClick={handleNextStep}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={!canProceedToNextStep()}
                sx={{
                  bgcolor: '#039582',
                  '&:hover': { bgcolor: '#027a6b' },
                  textTransform: 'none'
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handlePlaceOrder}
                variant="contained"
                startIcon={<CheckCircleIcon />}
                disabled={!canProceedToNextStep()}
                sx={{
                  bgcolor: '#039582',
                  '&:hover': { bgcolor: '#027a6b' },
                  textTransform: 'none'
                }}
              >
                Submit Order
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editOrderOpen} onClose={() => setEditOrderOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
          Edit Order {editingOrder?.orderNumber}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editingOrder && (
            <Stack spacing={3}>
              <Alert severity="info">
                You can only edit orders that haven't been shipped yet.
              </Alert>

              <Typography variant="subtitle2">Shipping Address</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={editOrderData.shippingAddress?.name || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, name: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={editOrderData.shippingAddress?.address1 || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, address1: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={editOrderData.shippingAddress?.city || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, city: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={editOrderData.shippingAddress?.state || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, state: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="ZIP"
                    value={editOrderData.shippingAddress?.zipCode || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, zipCode: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={editOrderData.shippingAddress?.phone || ''}
                    onChange={(e) => setEditOrderData({
                      ...editOrderData,
                      shippingAddress: { ...editOrderData.shippingAddress, phone: e.target.value }
                    })}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={editOrderData.notes || ''}
                onChange={(e) => setEditOrderData({ ...editOrderData, notes: e.target.value })}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOrderOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
            <Button
            onClick={handleEditOrder}
            variant="contained"
            disabled={!editingOrder || !canEditOrder(editingOrder)}
            sx={{
              bgcolor: '#039582',
              '&:hover': { bgcolor: '#027a6b' },
              textTransform: 'none'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepOrders;
